// Plant-Maintenance-Modul-Shell fuer den gineex-one-Monolithen.
//
// Portierung aus dem Vite-Prototyp (gineex-pm/frontend/src/App.jsx). Was sich aendert:
//   - Die eigenstaendige Chrome faellt WEG: App-Header, Theme-Switcher, Sprach-Umschalter und die
//     DEMO-Rollen-Leiste liefert jetzt die Monolith-Shell (App.js wrappt alles in ThemeProvider/
//     LanguageProvider und stellt Header/Logout). Hier bleibt nur eine Zurueck-Affordanz (onBack)
//     plus der PM-Titel/Untertitel-Block im Monolith-Idiom.
//   - Das INNERE Routing/State bleibt erhalten: home -> search -> Wizards/Listen/Detail.
//   - Statt GET /api/pm/me?role=... wird das Profil aus dem authentifizierten `user`-Prop abgeleitet
//     (deriveProfile). Funktionsrechte sind autoritativ fuers Gating; homeLayout ist nur Anordnung.
//     Ein kleiner interner Rollen-Umschalter (Mechaniker/Meister) bleibt fuer die Demo, vorbelegt
//     aus dem `user`-Mapping.
//   - Styling folgt dem Monolith-Idiom: Verzweigung auf isDarkMode aus useTheme(), zwei konkrete
//     Tailwind-v3-Klassen-Strings (Glas/dunkel, solid/hell). PM-Akzentfarbe ist amber.
//
// Props (App.js -> registry fast-path): { user, onBack, showSuccess, showError }. Heute werden nur
// `user` und `onBack` benoetigt; showSuccess/showError sind verdrahtet und stehen fuer echtes
// PM-UI bereit, ohne App.js zu aendern.
import React, { useEffect, useMemo, useState } from 'react';
import { Wrench, ArrowLeft, ShieldAlert, AlertTriangle } from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';
import { me as fetchMe } from './api';
import HomeScreen from './components/HomeScreen';
import NewOrderWizard from './components/NewOrderWizard';
import NewNotificationWizard from './components/NewNotificationWizard';
import OrdersList from './components/OrdersList';
import OrderDetail from './components/OrderDetail';
import MaterialSearch from './components/MaterialSearch';
import NotificationsScreen, { NotificationDetail } from './components/NotificationsScreen';
import PreventiveScreen from './components/PreventiveScreen';
import ReportsScreen from './components/reports/ReportsScreen';

// Home-Aktions-Key -> View. Die Auftragslisten teilen sich EINE Komponente, parametrisiert ueber mode.
// (Identisch zum Prototyp ACTION_VIEWS in App.jsx.)
const ACTION_VIEWS = {
  'create-order':        { name: 'new-order' },
  'create-notification': { name: 'new-notification' },
  'my-orders':           { name: 'orders', mode: 'my' },
  'backlog':             { name: 'orders', mode: 'backlog' },
  'approvals':           { name: 'orders', mode: 'approvals' },
  'material-search':     { name: 'materials' },
  'notifications':       { name: 'notifications' },
  'preventive':          { name: 'preventive' },
  'reports':             { name: 'reports' },
};

// PM-Profil-Presets — SOFORT-Startwert und Offline-Fallback. Autoritativ ist GET /api/pm/me
// (backend profiles.js): nach Mount/Rollenwechsel konvergiert das Profil per Fetch auf die
// Server-Antwort — der Spiegel hier verhindert nur einen Lade-Flash und traegt die UI, wenn der
// Fetch scheitert. PIN: bei Aenderungen an backend/modules/plant-maintenance/profiles.js diesen
// Spiegel nachziehen (id, label, scope, functionRights, home_layout->homeLayout).
// Die functionRights gaten, was erlaubt ist (Sicherheit); homeLayout ist die Default-Anordnung
// (Praeferenz). Per-User-Overrides sind auf v2 verschoben (authorization-model.md).
// scope.plants ist ein ARRAY (Lager-Muster, fail-closed): der Werks-Umschalter im Kopf erscheint
// nur bei >1 Werk; sonst ist das einzige Werk der harte Scope.
//
// The mirror carries NO org values (2026-09-25, PM code audit 34). Until 2026-09-24 every preset
// here listed plants 1000/1140, work centres PM-TECH/ASSEMBLY and order types PM02/PM03 — the
// retired SBX demo world. None of them exists on S23, so whenever /me did not answer the screens
// rendered a plant the user does not have and cannot write to, and the first failure appeared much
// later, in SAP, as "plant does not exist". An empty scope says the same thing honestly: the
// switcher stays hidden, the banner explains, and every write is refused by the server's own scope
// check rather than by a coincidence of which codes were hardcoded here.
const EMPTY_SCOPE = { plants: [], plantNames: {}, workCenters: [], orderTypes: [] };
// canAdminister mirrors the server list and is false for every profile — no seeded role holds it
// and no /admin route is gated on it yet (see profiles.js, PM code audit finding 13); it is here
// so the mirror does not silently diverge from the /me payload.
const PM_PROFILES = {
  mechanic: {
    id: 'mechanic',
    label: 'Mechanic',
    scope: EMPTY_SCOPE,
    functionRights: {
      canCreateOrder: true,
      canConfirm: true,
      canCreateNotification: true,
      canAddComponents: true,
      canApprove: false,
      canTechnicallyComplete: false,
      canBusinessComplete: false,
      canManageUsers: false,
      canAdminister: false,
      canManageMasterData: false,
      canManagePreventive: false,
      canViewCosts: false,
    },
    homeLayout: ['create-order', 'create-notification', 'notifications', 'my-orders', 'material-search'],
  },
  supervisor: {
    id: 'supervisor',
    label: 'Supervisor',
    scope: EMPTY_SCOPE,
    functionRights: {
      canCreateOrder: true,
      canConfirm: false,
      canCreateNotification: true,
      canAddComponents: false,
      canApprove: true,
      canTechnicallyComplete: true,
      canBusinessComplete: false,
      canManageUsers: true,
      canAdminister: false,
      canManageMasterData: true,
      canManagePreventive: true,
      canViewCosts: true,
    },
    homeLayout: ['backlog', 'approvals', 'notifications', 'preventive', 'reports', 'my-orders', 'create-order'],
  },
  controller: {
    id: 'controller',
    label: 'Controlling / Maintenance costs',
    scope: EMPTY_SCOPE,
    functionRights: {
      canCreateOrder: false,
      canConfirm: false,
      canCreateNotification: false,
      canAddComponents: false,
      canApprove: false,
      canTechnicallyComplete: false,
      canBusinessComplete: true,
      canManageUsers: false,
      canAdminister: false,
      canManageMasterData: false,
      canManagePreventive: false,
      canViewCosts: true,
    },
    homeLayout: ['reports', 'backlog', 'my-orders'],
  },
};

/**
 * Bildet die PM-Rolle aus dem authentifizierten Monolith-`user` ab.
 * Heute traegt der Monolith-User noch keine PM-spezifische Rolle — wir mappen ueber die generische
 * Rolle: 'supervisor'/'admin' -> Meister-Profil, sonst -> Mechaniker. (Ersetzt den /me?role-Fetch.)
 * @param {object} [user]
 * @returns {'mechanic'|'supervisor'}
 */
function deriveRole(user) {
  const role = user?.role;
  if (role === 'supervisor' || role === 'admin') return 'supervisor';
  return 'mechanic';
}

/**
 * Baut das `me`-Objekt (gleiche Form wie die /me-Antwort) aus Profil + Monolith-User, plus
 * `activePlant`: das aktuell gewaehlte Werk als harter Scope fuer alle Screens (Lager-Muster).
 * user.name/username/sapUser sind reine Anzeige und kommen direkt aus dem Monolith-User.
 * @param {object} profile — Profil in /me-Form (Preset oder Server-Antwort)
 * @param {object} [user]
 * @param {string} [activePlant]
 */
function buildMe(profile, user, activePlant, features, sap) {
  return {
    user: {
      name: user?.name || user?.username || profile.label,
      sapUser: user?.sapUser || user?.username || '',
    },
    profile,
    // Fail-closed: nur ein Werk aus dem Profil-Scope kann aktiv sein; Default = erstes Scope-Werk.
    // null when the scope is empty (the mirror preset before /me answers): every screen then has
    // NO plant rather than an invented one, and the banner below says so.
    activePlant: profile.scope.plants.includes(activePlant) ? activePlant : (profile.scope.plants[0] || null),
    // Unternehmensweite Feature-Flags aus /me — fail-closed (alles aus), bis der Server antwortet.
    // Experimentelle Features (z. B. Spracheingabe) bleiben so dunkel, der Spiegel kennt keine Flags.
    features: features || { voice: false, loading: true }, // bis /me antwortet: aus, neutraler Hinweis
    // Welcher Adapter antwortet (aus /me). Fail-honest: solange der Server nicht geantwortet hat,
    // gilt "nicht live" — die UI behauptet lieber Demo als faelschlich "in SAP angelegt".
    sap: sap || { mode: 'mock', live: false },
  };
}

/**
 * Modul-Shell. Optionales `seedEquipment` ist der dokumentierte Einstiegspunkt fuer Modul-zu-Modul-
 * Handoffs (SchnellBanf-Muster aus Lager->Beschaffung): ein externes Modul (z. B. Lager, Material-
 * Detail) kann PM direkt im Auftrags-Wizard mit vorbelegtem Geraet oeffnen —
 *   <PlantMaintenanceView user={user} onBack={...} seedEquipment={{ equipmentId, description,
 *     functionalLocation, workCenter? }} />
 * Kein App.js-Umbau noetig, bis der erste Aufrufer es verdrahtet.
 */
const PlantMaintenanceView = ({ user, onBack, seedEquipment = null }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  // Rolle aus dem echten User vorbelegen; der interne Selektor erlaubt das Demo-Umschalten.
  const [role, setRole] = useState(() => deriveRole(user));
  const [view, setView] = useState(() => (seedEquipment ? { name: 'new-order', seed: seedEquipment } : { name: 'home' }));
  // Aktives Werk (nur relevant bei Profilen mit >1 Werk; sonst unsichtbar auf plants[0] fixiert).
  const [plant, setPlant] = useState(null);
  // Profil: Spiegel-Preset sofort, Server-Antwort autoritativ sobald da (Konvergenz statt Drift).
  const [profile, setProfile] = useState(() => PM_PROFILES[deriveRole(user)] || PM_PROFILES.mechanic);
  // Feature-Flags: fail-closed (null -> alles aus), bis /me sie liefert.
  const [features, setFeatures] = useState(null);
  // Adapter-Modus aus /me (mock|sbx|real) — steuert nur ehrliche Hinweistexte, kein Gating.
  const [sapInfo, setSapInfo] = useState(null);
  // Zugewiesene Rollen (mehrere Huete). Bis /me antwortet: nur die abgeleitete Rolle, also kein
  // Umschalter — lieber gar keine Auswahl als eine, die der Server danach ablehnt.
  const [availableRoles, setAvailableRoles] = useState(() => [{ id: deriveRole(user), label: '' }]);
  // Blocked (Server-/me-Feld): 'noPlants' = PM-Rolle ohne zugewiesene Werke (seeded Profile
  // starten so), 'noProfile' = strict mode ohne PM-Profil. Der Server beantwortet dann ALLES
  // ausser /me mit 403 — die UI zeigt die Erklaerung statt des Moduls (kein toter Klickpfad).
  const [blocked, setBlocked] = useState(null);
  // /me unreachable: the mirror preset carries the UI on — and its plants are the RETIRED SBX demo
  // plants ('1000'/'1140'), which do not exist on the live system (PM code audit 34). Proceeding
  // silently means the user works on an invented plant and never learns it, so the fallback gets a
  // visible banner plus a retry. Deliberately NOT the blocked screen: the module is not locked,
  // only the answer is missing, and "PM access not set up yet" would be the wrong diagnosis —
  // the same mistake audit 25 describes.
  const [meError, setMeError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let alive = true;
    fetchMe(user, role)
      .then((res) => {
        if (!alive) return;
        setMeError(false);
        setBlocked(res?.blocked || null);
        if (res?.blocked) return; // Profil/Scope nicht uebernehmen — der blockierte Zustand traegt
        if (res?.profile?.scope?.plants) setProfile(res.profile);
        if (res?.features) setFeatures(res.features);
        if (res?.sap) setSapInfo(res.sap);
        if (Array.isArray(res?.availableRoles) && res.availableRoles.length) {
          setAvailableRoles(res.availableRoles);
          // Der Server kann die gewuenschte Rolle ablehnen (nicht zugewiesen) — dann ist
          // res.profile.id die tatsaechlich aktive. UI nachziehen statt still divergieren.
          if (res.profile?.id && res.profile.id !== role) setRole(res.profile.id);
        }
      })
      .catch(() => { if (alive) setMeError(true); });
    return () => { alive = false; };
  }, [role, user, retryKey]);

  // me wird aus Profil + User + Werk + Flags abgeleitet. Neu berechnen bei Wechsel.
  const me = useMemo(() => buildMe(profile, user, plant, features, sapInfo), [profile, user, plant, features, sapInfo]);

  // --- Inneres Routing (identisch zum Prototyp, ohne react-router) ---
  const goPmHome = () => setView({ name: 'home' });
  const startOrder = (equipment = null) => setView({ name: 'new-order', seed: equipment });
  const onAction = (key) => setView(ACTION_VIEWS[key] || { name: 'home' });
  const openOrder = (orderId, fromMode) => setView({ name: 'order-detail', orderId, fromMode });

  // Rolle umschalten setzt immer auf die PM-Startseite zurueck (wie im Prototyp), verwirft die
  // Werksauswahl (das neue Profil kann einen anderen Scope haben — buildMe fixiert fail-closed)
  // und laedt sofort das Spiegel-Preset; der /me-Fetch oben konvergiert danach auf den Server.
  const onRole = (r) => {
    setRole(r);
    setPlant(null);
    setProfile(PM_PROFILES[r] || PM_PROFILES.mechanic);
    setView({ name: 'home' });
  };

  const body = () => {
    switch (view.name) {
      case 'new-order':
        return <NewOrderWizard me={me} user={user} seedEquipment={view.seed || null} onPlantChange={setPlant} onDone={goPmHome} onCancel={goPmHome} onOpenOrder={(orderId) => openOrder(orderId)} />;
      case 'new-notification':
        return <NewNotificationWizard me={me} user={user} seedEquipment={view.seed || null} onPlantChange={setPlant} onDone={goPmHome} onCancel={goPmHome} />;
      case 'orders':
        return <OrdersList me={me} user={user} mode={view.mode} onOpen={(id) => openOrder(id, view.mode)} onBack={goPmHome} />;
      case 'order-detail':
        return (
          <OrderDetail
            me={me}
            user={user}
            orderId={view.orderId}
            onBack={() => setView(view.fromMode ? { name: 'orders', mode: view.fromMode } : { name: 'home' })}
          />
        );
      case 'materials':
        return <MaterialSearch me={me} user={user} onBack={goPmHome} />;
      case 'notifications':
        return <NotificationsScreen me={me} user={user} onOpen={(id) => setView({ name: 'notification-detail', notificationId: id })} onBack={goPmHome} />;
      case 'notification-detail':
        return (
          <NotificationDetail
            me={me}
            user={user}
            notificationId={view.notificationId}
            onBack={() => setView({ name: 'notifications' })}
            onOpenOrder={(orderId) => openOrder(orderId)}
          />
        );
      case 'preventive':
        return <PreventiveScreen me={me} user={user} onBack={goPmHome} />;
      case 'reports':
        return <ReportsScreen me={me} user={user} onBack={goPmHome} onOpenOrder={(orderId) => openOrder(orderId)} />;
      default:
        return <HomeScreen me={me} user={user} onStartOrder={startOrder} onAction={onAction} onPlantChange={setPlant} />;
    }
  };

  return (
    <div className={`flex-1 min-h-screen ${isDarkMode
      ? 'bg-gradient-to-br from-slate-900 via-gray-900 to-slate-800'
      : 'bg-[#F8FAFC]'
    }`}>

      {/* Kopf-Band — Glas (dunkel) / solid (hell). Liefert die Zurueck-Affordanz + PM-Titelblock. */}
      <div className="relative">
        <div className={`absolute inset-0 ${isDarkMode
          ? 'bg-gray-900/20 border-b border-white/10 backdrop-blur-2xl shadow-[0_8px_32px_rgba(0,0,0,0.3)]'
          : 'bg-white border-b border-slate-200'
        }`}></div>

        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* flex-wrap: auf schmalen Screens brechen Werks-/Rollen-Umschalter in eine zweite Zeile,
              statt zu verschwinden — Meister-Flows muessen auch am Telefon testbar sein. */}
          <div className="flex items-center gap-4 flex-wrap">
            {/* Zurueck-Button (onBack -> App.js goBack). t('common.back') ist ein geteilter Core-Key. */}
            <button
              onClick={onBack}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${isDarkMode
                ? 'bg-white/10 hover:bg-white/15 text-white/80 hover:text-white border border-white/10'
                : 'bg-slate-100 hover:bg-slate-200 text-slate-600 hover:text-slate-900'
              }`}
            >
              <ArrowLeft className="w-4 h-4" />
              {t('common.back')}
            </button>

            {/* Titel-Bereich — amber-Akzent-Chip + Verlaufs-Titel. */}
            <div className="flex items-center gap-4 flex-1 min-w-0">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${isDarkMode
                ? 'bg-gradient-to-br from-amber-500/70 via-amber-600/60 to-amber-700/70 shadow-[0_8px_32px_rgba(0,0,0,0.3)] border border-white/20'
                : 'bg-amber-100'
              }`}>
                <Wrench className={`w-6 h-6 ${isDarkMode ? 'text-white/90' : 'text-amber-600'}`} />
              </div>
              <div className="min-w-0">
                <h1 className={`text-2xl font-bold truncate ${isDarkMode
                  ? 'bg-gradient-to-r from-white via-amber-200 to-white bg-clip-text text-transparent'
                  : 'text-slate-900'
                }`}>
                  {t('pm.title')}
                </h1>
                <p className={`text-sm mt-0.5 truncate ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
                  {t('pm.subtitle')}
                </p>
              </div>
            </div>

            {/* Der Werks-Umschalter lebt NICHT mehr hier im Kopf, sondern als IMMER sichtbares
                PlantSelect rechts oben auf Home und in den Anlege-Wizards (Lager-Muster). */}

            {/* Rollen-Umschalter fuer Menschen mit mehreren Huetten: angeboten werden NUR die
                Rollen, die der Server dem Benutzer zuweist (/me availableRoles aus
                permission_profiles.pm_role). Bei genau einer Rolle verschwindet der Umschalter —
                dann gibt es nichts zu waehlen. Das Gating bleibt serverseitig (requireRight);
                dieser Schalter waehlt nur, unter welchem Hut gearbeitet wird. */}
            {availableRoles.length > 1 && (
              <div className={`flex items-center rounded-full overflow-hidden text-xs font-semibold flex-shrink-0 ${isDarkMode
                ? 'bg-white/[0.08] border border-white/10'
                : 'bg-slate-100 border border-slate-200'
              }`}>
                {availableRoles.map((r) => (
                  <RoleBtn key={r.id} active={role === r.id} isDarkMode={isDarkMode} onClick={() => onRole(r.id)}>
                    {t(`pm.role.${r.id}`) === `pm.role.${r.id}` ? r.label : t(`pm.role.${r.id}`)}
                  </RoleBtn>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Inhaltsspalte — touch-first, schmal (wie der Prototyp: max-w-md/2xl). */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* /me did not answer: profile and feature flags are placeholders from the mirror preset,
            not the server's, and the scope is EMPTY — the preset no longer invents plants (audit
            34). So there is no plant to name here; the banner says the scope is missing and offers
            the retry. Deliberately not the blocked screen: the module is not locked, the answer is
            missing, and "PM access not set up yet" would be the wrong diagnosis (audit 25). */}
        {!blocked && meError && (
          <div className={`mb-4 rounded-2xl border px-4 py-3 flex items-center gap-3 ${isDarkMode
            ? 'bg-amber-500/10 border-amber-400/30 text-amber-200'
            : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <p className="text-sm flex-1 min-w-0">
              {t('pm.scope.placeholder')}
              {me.activePlant ? ` · ${t('pm.notif.plant')} ${me.activePlant}` : ''}
            </p>
            <button
              onClick={() => setRetryKey((k) => k + 1)}
              className="text-sm font-semibold underline flex-shrink-0"
            >
              {t('pm.common.retry')}
            </button>
          </div>
        )}
        {blocked ? (
          <div className={`rounded-2xl border p-6 text-center ${isDarkMode
            ? 'bg-white/[0.04] border-white/10' : 'bg-white border-slate-200'}`}>
            <ShieldAlert className={`w-10 h-10 mx-auto mb-3 ${isDarkMode ? 'text-amber-300' : 'text-amber-500'}`} />
            <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              {t('pm.blocked.title')}
            </h2>
            <p className={`mt-2 text-sm max-w-md mx-auto ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
              {t(blocked === 'noProfile' ? 'pm.blocked.noProfile' : 'pm.blocked.noPlants')}
            </p>
          </div>
        ) : body()}
      </main>
    </div>
  );
};

// Kleiner Rollen-Tab im Kopf — aktiv = amber-Akzent, sonst gedaempft.
function RoleBtn({ active, isDarkMode, onClick, children }) {
  const cls = active
    ? (isDarkMode ? 'bg-amber-500/20 text-amber-200' : 'bg-amber-500 text-white')
    : (isDarkMode ? 'text-white/60 hover:text-white/80' : 'text-slate-600 hover:text-slate-900');
  return (
    <button onClick={onClick} className={`px-3 py-1.5 transition-colors ${cls}`} aria-pressed={active}>
      {children}
    </button>
  );
}

export default PlantMaintenanceView;
